/**
 * Who may see money.
 *
 * Cost is school financial information, so it needs two independent conditions,
 * NOT one:
 *
 *   1. the org bought costing        — the `finance` org feature flag
 *   2. the viewer is allowed to see it — an Admin or SLT role
 *
 * Before this helper existed, `OverviewStats` and `FuelBreakdownCard` each
 * checked only (1), so on a finance-enabled org a student opening `/power` —
 * guarded by `MEMBER_ROLES`, which includes `Student Role` — saw the site's
 * spend inc. VAT. Both now call this, for the same reason `lib/roles.js` exists:
 * two call sites that must agree should not each carry their own copy of the
 * rule.
 *
 * The API applies the identical gate in `power_backend/core/cost_visibility.py`
 * (`can_see_cost`, `COST_VISIBLE_ROLES`), used by the power overview and the
 * quiz, so a student's overview payload has no cost fields to leak and a pupil
 * is never asked a money question. This helper is the presentation half of that
 * pair, never the whole defence.
 */
// Relative paths with explicit extensions (rather than the `@/` alias) so this
// module loads under plain `node --test` as well as through Vite — same reason
// as `orgFeatureFlags.js` and `utils/hasRole.js`.
import { isFeatureFlagEnabled } from '../features/dashboard/site-admin/orgFeatureFlags.js'
import { hasRole } from '../utils/hasRole.js'
import { LOCATION_MEMBER_ROLES, SLT_ROLES } from './roles.js'

/**
 * Flavours whose location members see the cost of their own Locations: a business site
 * manager and an estate tenant do; a school housemaster does not (like a Teacher). Mirrors
 * `COST_VISIBLE_LOCATION_MEMBER_FLAVOURS` in backend core/cost_visibility.py.
 */
export const LOCATION_MEMBER_COST_FLAVOURS = ['business', 'estate']

export const canSeeCost = (user, activeOrg) => {
    if (!isFeatureFlagEnabled(activeOrg, 'finance') || !user) return false
    if (user.locationAccess || hasRole(user, LOCATION_MEMBER_ROLES)) {
        return LOCATION_MEMBER_COST_FLAVOURS.includes(activeOrg?.flavour)
    }
    return hasRole(user, SLT_ROLES)
}

export default canSeeCost
